import { useEffect, useState } from "react"
import { url as cmsUrl } from "aldoo-ra/CMS/url"

// Shared theme-application primitives for any page that renders a UserShareTheme:
//   • ShareLandingPage (recipient downloads files)
//   • ReceivePage (sender uploads files to a receive link)
//
// The owner picks one of their themes (background image + 4 colors); both pages
// reuse the same render pipeline so a Pro user's branding looks identical to
// the people they're sending to and the people sending to them.

export function hexToRgbTriplet(hex) {
    if (typeof hex !== "string") return null
    const m = hex.trim().replace(/^#/, "")
    const full = m.length === 3 ? m.split("").map((c) => c + c).join("") : m
    if (!/^[0-9a-fA-F]{6}$/.test(full)) return null
    const n = parseInt(full, 16)
    return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`
}

export function buildThemeStyle(theme) {
    if (!theme) return undefined
    const style = {}
    const bgT = hexToRgbTriplet(theme.color_background)
    const textT = hexToRgbTriplet(theme.color_text)
    const primT = hexToRgbTriplet(theme.color_primary)
    const btnT = hexToRgbTriplet(theme.color_button_text)
    if (bgT) { style["--color-canvas"] = bgT; style["--color-paper"] = bgT }
    if (textT) { style["--color-text"] = textT; style["--color-text_secondary"] = textT }
    if (primT) {
        style["--color-primary"] = primT
        style["--color-primary_lt"] = primT
        style["--color-primary_dk"] = primT
        style["--color-primary_hov"] = primT
    }
    if (btnT) style["--color-button-text"] = btnT
    return Object.keys(style).length ? style : undefined
}

export function ThemeOverrides({ theme }) {
    if (!theme) return null
    const bg = theme.color_background
    const text = theme.color_text
    const prim = theme.color_primary
    const btn = theme.color_button_text || "#FFFFFF"
    const rules = []
    if (bg) rules.push(`
        [data-share-theme] [class*="bg-paper"] { background-color: ${bg} !important; }
    `)
    if (text) rules.push(`
        [data-share-theme] [class*="text-text"]:not([class*="text-text_"]) { color: ${text} !important; }
    `)
    if (prim) rules.push(`
        [data-share-theme] [class*="bg-primary"]:not([class*="bg-primary_"]) { background-color: ${prim} !important; }
        [data-share-theme] [class*="hover\\:bg-primary"]:hover { background-color: ${prim} !important; }
    `)
    rules.push(`
        [data-share-theme] [class*="bg-primary"]:not([class*="bg-primary_"]) { color: ${btn} !important; }
        [data-share-theme] .text-white { color: ${btn} !important; }
    `)
    // Signin / magic-link dialog uses gray_* utilities that aren't tokenized.
    // Map them onto the theme so the email input chip, placeholder, and the
    // small "or sign in with" text remain readable on any owner theme. We
    // use a lifted shade of the background instead of the raw background so
    // the chip stays visible against the page surface.
    if (bg) rules.push(`
        [data-share-theme] .bg-gray_100 { background-color: rgb(var(--color-neutral) / 1) !important; }
        [data-share-theme] .bg-gray_1 { background-color: rgb(var(--color-neutral_1) / 1) !important; }
    `)
    // Email icon (the CMS svg) is an <img> so it can't pick up text color.
    // Flatten its colors and let it inherit luminance from the parent text
    // via mix-blend-mode so it stays legible on any owner theme.
    rules.push(`
        [data-share-theme] img[alt*="Email"],
        [data-share-theme] img[src*="icon_email"] {
            filter: grayscale(1) contrast(0.4);
            mix-blend-mode: luminosity;
            opacity: 0.7;
        }
    `)
    if (text) rules.push(`
        [data-share-theme] input::placeholder,
        [data-share-theme] textarea::placeholder { color: ${text} !important; opacity: 0.55 !important; }
        [data-share-theme] input { color: ${text} !important; }
        [data-share-theme] .text-gray_1,
        [data-share-theme] .text-gray_700,
        [data-share-theme] .text-gray_100 { color: ${text} !important; }
    `)
    return <style>{rules.join("\n")}</style>
}

export function themeBackgroundUrl(theme) {
    const ref = theme?.background_image
    const one = Array.isArray(ref) ? ref[0] : ref
    if (!one) return null
    try { return cmsUrl(one) } catch { return null }
}

// Lift the background triplet toward white by `amount` (0..1). Borders and
// neutral surfaces become tinted shades of the background — keeps them
// visible without resorting to text-colored outlines (yellow text on yellow
// border was unreadable in custom themes).
function lift(triplet, amount) {
    if (!triplet) return null
    const [r, g, b] = triplet.split(" ").map((n) => parseInt(n, 10))
    const mix = (c) => Math.min(255, Math.round(c + (255 - c) * amount))
    return `${mix(r)} ${mix(g)} ${mix(b)}`
}

// Pushes theme CSS variables onto <body> so every Tailwind utility that reads
// `var(--color-x)` (including alpha modifiers like `bg-paper/85`) repaints in
// the owner's colors — even when content is rendered through a portal.
export function useThemeOnBody(theme) {
    useEffect(() => {
        if (typeof document === "undefined" || !theme) return
        const body = document.body
        body.setAttribute("data-share-theme", "1")
        const set = (k, v) => v && body.style.setProperty(k, v)
        const bgT = hexToRgbTriplet(theme.color_background)
        const textT = hexToRgbTriplet(theme.color_text)
        const primT = hexToRgbTriplet(theme.color_primary)
        const btnT = hexToRgbTriplet(theme.color_button_text)
        set("--color-canvas", bgT)
        set("--color-paper", bgT)
        set("--color-text", textT)
        set("--color-text_secondary", textT)
        set("--color-bordered", lift(bgT, 0.22))
        set("--color-neutral", lift(bgT, 0.08))
        set("--color-neutral_1", lift(bgT, 0.14))
        set("--color-neutral_2", lift(bgT, 0.22))
        if (primT) {
            set("--color-primary", primT)
            set("--color-primary_lt", primT)
            set("--color-primary_dk", primT)
            set("--color-primary_hov", primT)
        }
        if (btnT) set("--color-button-text", btnT)
        return () => {
            body.removeAttribute("data-share-theme")
            const props = [
                "--color-canvas", "--color-paper", "--color-text", "--color-text_secondary",
                "--color-bordered", "--color-neutral", "--color-neutral_1", "--color-neutral_2",
                "--color-primary", "--color-primary_lt", "--color-primary_dk", "--color-primary_hov",
                "--color-button-text",
            ]
            for (const p of props) body.style.removeProperty(p)
        }
    }, [theme])
}

// Wraps page content in the same shell ShareLandingPage uses: full-bleed
// theme background image + scoped Tailwind overrides. Without a theme, a
// neutral canvas surface so the page never sits on raw page bg.
export function ThemeShell({ theme, bg: bgOverride, children, fallbackClass = "bg-canvas" }) {
    const bg = bgOverride || themeBackgroundUrl(theme)
    const [bgLoaded, setBgLoaded] = useState(false)
    useEffect(() => {
        if (!bg) return
        setBgLoaded(false)
        const img = document.createElement("img")
        img.onload = () => setBgLoaded(true)
        img.src = bg
    }, [bg])
    useThemeOnBody(theme)
    return (
        <div
            data-share-theme={theme ? "1" : undefined}
            className={`relative min-h-screen overflow-hidden ${theme ? "bg-canvas" : fallbackClass}`}
            style={buildThemeStyle(theme)}
        >
            <ThemeOverrides theme={theme} />
            {bg && (
                <div
                    className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-[2000ms] ease-out ${bgLoaded ? "opacity-100" : "opacity-0"}`}
                    style={{ backgroundImage: `url(${bg})` }}
                />
            )}
            <div className="relative z-10">{children}</div>
        </div>
    )
}
